/**
 * Production-ready logger utility for frontend application.
 * 
 * Provides environment-aware logging that only logs in development mode
 * to prevent console pollution in production.
 */

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';

  /**
   * Log debug message (only in development)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${message}`, context || '');
    }
  }

  /**
   * Log info message (only in development)
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Log warning message (always logged)
   */
  warn(message: string, context?: LogContext): void {
    console.warn(`[WARN] ${message}`, context || '');
  }

  /**
   * Log error message (always logged)
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorContext = {
      ...context,
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : error,
    };

    console.error(`[ERROR] ${message}`, errorContext);

    // In production, send error to monitoring service
    if (!this.isDevelopment) {
      this.sendToMonitoring(message, error, context);
    }
  }

  /**
   * Send error to monitoring service (Sentry, LogRocket, etc.)
   * 
   * This is a placeholder that can be integrated with your monitoring service.
   * Example integrations:
   * - Sentry: Sentry.captureException(error, { extra: context })
   * - LogRocket: LogRocket.captureException(error, { extra: context })
   */
  private sendToMonitoring(
    message: string,
    error: Error | unknown,
    context?: LogContext
  ): void {
    // Placeholder for monitoring service integration
    // Example: if (window.Sentry) { window.Sentry.captureException(error, { extra: context }); }
    
    // For now, we could send to a backend logging endpoint
    // if (window.fetch) {
    //   fetch('/api/logs/error', {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify({ message, error, context }),
    //   }).catch(() => {
    //     // Silently fail if logging endpoint is unavailable
    //   });
    // }
  }
}

export const logger = new Logger();

